
/**
 * Simple in-memory cache service for server-side data caching
 */
export class CacheService {
  constructor() {
    this.cache = new Map();
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      evictions: 0
    };
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!global._cacheServiceInstance) {
      global._cacheServiceInstance = new CacheService();
    }
    return global._cacheServiceInstance;
  }

  /**
   * Get item from cache with TTL validation
   * @param {string} key - Cache key
   * @param {number} [ttl] - Time to live in ms (optional)
   * @returns {object|null} - Cached value or null if not found/expired
   */
  get(key, ttl) {
    const item = this.cache.get(key);
    
    if (!item) {
      this.stats.misses++;
      return null;
    }
    
    const now = Date.now();
    
    // Check if item is expired (if TTL is provided)
    if (ttl && item.timestamp && (now - item.timestamp > ttl)) {
      this.stats.evictions++;
      this.cache.delete(key);
      return null;
    }
    
    this.stats.hits++;
    return item;
  }

  /**
   * Check if cache has valid item
   * @param {string} key - Cache key
   * @param {number} [ttl] - Time to live in ms (optional)
   * @returns {boolean} - Whether cache has valid item
   */
  has(key, ttl) {
    const item = this.cache.get(key);
    
    if (!item) {
      return false;
    }
    
    if (ttl && item.timestamp) {
      const now = Date.now();
      return (now - item.timestamp <= ttl);
    }
    
    return true;
  }

  /**
   * Set item in cache
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {object} [options] - Cache options
   * @param {number} [options.expiration] - Expiration time in ms
   */
  set(key, data, options = {}) {
    this.stats.sets++;
    
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      options
    });
    
    // Set expiration if provided
    if (options.expiration) {
      setTimeout(() => {
        this.cache.delete(key);
      }, options.expiration);
    }
    
    return true;
  }

  /**
   * Delete item from cache
   * @param {string} key - Cache key
   */
  delete(key) {
    return this.cache.delete(key);
  }

  /**
   * Clear entire cache
   */
  clear() {
    this.cache.clear();
    return true;
  }

  /**
   * Delete all keys that match a pattern (regex)
   * @param {RegExp} pattern - Regex pattern to match keys
   */
  deletePattern(pattern) {
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return { ...this.stats };
  }
}

// Create a singleton instance for client-side use
const isBrowser = typeof window !== 'undefined';
const cacheInstance = isBrowser 
  ? new CacheService() 
  : (global._cacheServiceInstance || (global._cacheServiceInstance = new CacheService()));

// Export the singleton instance
export const cacheService = cacheInstance;
